import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Armchair, CalendarCheck, Mail, MessageSquare, Sparkles, UtensilsCrossed } from 'lucide-react'
import { useT } from '@/components/visitone/visitone-provider'
import { AccessPills } from '@/components/visitone/access-status'
import { ActivityHistory } from '@/components/visitone/activity-history'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { PageHeader } from '@/components/visitone/page-header'
import { AccessMessage, RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Field } from '@/components/ui/field'
import { NativeSelect } from '@/components/ui/native-select'
import { Notice } from '@/components/ui/notice'
import { Skeleton } from '@/components/ui/skeleton'
import { StatusPill } from '@/components/ui/status-pill'
import { useAccess } from '@/hooks/use-access'
import { useGuestHistory, useGuestRecord } from '@/hooks/use-guests'
import { usePageTitle } from '@/hooks/use-page-title'
import { isForbidden, isNotFound, type GuestNotice, type GuestProfile } from '@/lib/bff/guests'
import { formatCalendarDate } from '@/lib/format/tenant-date'
import {
  guestName,
  HISTORY_FILTERS,
  historyGroups,
  notShownLines,
  type HistoryContext,
  type HistoryFilter,
} from '@/lib/guests/guest-text'
import { GUEST_RECORD_FEATURES } from '@/lib/guests/guest-lists'
import { GUESTS_PATH, guestRecordPath } from '@/lib/routing'

const LINK_CLASS = 'font-medium text-action underline underline-offset-4'

const ICONS: HistoryContext['icons'] = {
  visit: <CalendarCheck />,
  sms: <MessageSquare />,
  email: <Mail />,
  moment: <Sparkles />,
  table: <Armchair />,
  order: <UtensilsCrossed />,
}

/* A link to another guest's record; the guest whose record this is, or someone with no record, is their name. */
function GuestLink({ guestId, name, current }: { guestId?: string; name: string; current: string }) {
  if (!guestId || guestId === current) return <>{name}</>
  return (
    <Link to={guestRecordPath(guestId)} className={LINK_CLASS}>
      {name}
    </Link>
  )
}

/*
 * One guest's record (VIS-460) at `/guests/:guestId`: Profile beside History
 * from `lg`, stacked below. An unknown guest, another tenant's guest (also
 * after a tenant switch) and a guest VisitOne no longer holds all show the
 * same No guest found, which never confirms that the guest exists. Contact
 * rows and technical details are present only when the BFF sent them.
 * Opening the record is recorded first (VIS-330): when that fails, or the
 * record fails for any other reason, the heading stays Guest record and
 * nothing about the guest is shown. History waits for the record's `viewId`.
 */
export function GuestRecordSurface() {
  const t = useT()
  const navigate = useNavigate()
  const { guestId = '' } = useParams()
  const { tenantName, pageStatus, hasFeature } = useAccess()
  const showHistory = hasFeature(GUEST_RECORD_FEATURES.guestVisitHistory)
  const record = useGuestRecord(guestId)
  const history = useGuestHistory(guestId, showHistory ? record.record?.viewId : undefined)
  const loaded = record.record?.profile.guestId

  const focused = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!loaded || focused.current === loaded) return
    focused.current = loaded
    const frame = requestAnimationFrame(() => {
      const heading = document.querySelector<HTMLElement>('main h1')
      if (!heading) return
      heading.setAttribute('tabindex', '-1')
      heading.focus()
    })
    return () => cancelAnimationFrame(frame)
  }, [loaded])

  if (isForbidden(record.error)) return <RefusedSurface />
  if (isNotFound(record.error)) {
    return (
      <AccessMessage
        title={t('guests.record.notFoundTitle')}
        body={t('guests.record.notFoundBody', { tenant: tenantName })}
        action={
          <Button variant="primary" onClick={() => navigate(GUESTS_PATH)}>
            {t('guests.record.findGuest')}
          </Button>
        }
      />
    )
  }
  if (record.error) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader
          breadcrumb={[{ label: t('guests.title'), to: GUESTS_PATH }, t('guests.record.breadcrumb')]}
          title={t('guests.record.breadcrumb')}
        />
        {record.isValidating ? (
          <Card>
            <CardContent className="flex flex-col gap-3" aria-busy="true">
              <p className="sr-only" role="status" aria-live="polite">
                {t('guests.record.loading')}
              </p>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72 max-w-full" />
              <Skeleton className="h-4 w-64 max-w-full" />
            </CardContent>
          </Card>
        ) : (
          <OperationFeedback
            state="error"
            title={t('guests.record.loadFailedTitle')}
            detail={t('guests.record.loadFailedBody')}
            onRetry={() => void record.revalidate()}
          />
        )}
      </div>
    )
  }
  if (!record.record) return <RecordLoading />

  const { profile, notices, unavailable, timeZone } = record.record
  const name = guestName(t, profile.name)

  return (
    <div className="flex flex-col gap-1">
      <PageHeader
        breadcrumb={[{ label: t('guests.title'), to: GUESTS_PATH }, name]}
        title={name}
        status={<AccessPills status={pageStatus('guests')} />}
      />
      <div className={showHistory ? 'grid items-start gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]' : 'max-w-xl'}>
        <ProfileCard
          profile={profile}
          membershipsUnavailable={unavailable.includes('memberships')}
          onRetry={() => void record.revalidate()}
        />
        {showHistory ? (
          <HistoryCard guestId={profile.guestId} timeZone={timeZone} recordNotices={notices} history={history} hasFeature={hasFeature} />
        ) : null}
      </div>
    </div>
  )
}

function RecordLoading() {
  const t = useT()
  const { hasFeature } = useAccess()
  const showHistory = hasFeature(GUEST_RECORD_FEATURES.guestVisitHistory)
  usePageTitle(t('guests.record.loadingTitle'))
  return (
    <div className="flex flex-col gap-5">
      <h1 className="sr-only">{t('guests.record.loadingTitle')}</h1>
      <p className="sr-only" role="status" aria-live="polite">
        {t('guests.record.loading')}
      </p>
      <div aria-hidden="true" className="flex flex-col gap-2 pb-1">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-8 w-64 max-w-full" />
      </div>
      <div className={showHistory ? 'grid items-start gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]' : 'max-w-xl'}>
        <Card aria-hidden="true">
          <CardContent className="flex flex-col gap-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3.5 w-40" />
              </div>
            ))}
          </CardContent>
        </Card>
        {showHistory ? (
          <Card aria-hidden="true">
            <CardContent>
              <HistorySkeleton />
            </CardContent>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

function HistorySkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="flex gap-3">
          <Skeleton className="size-8 shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3.5 w-48 max-w-full" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      ))}
    </div>
  )
}

function ProfileRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-sm text-fg" style={{ fontWeight: 'var(--vo-label-font-weight)' }}>
        {label}
      </dt>
      <dd className="text-sm text-fg-muted wrap-anywhere">{children}</dd>
    </div>
  )
}

function ProfileCard({
  profile,
  membershipsUnavailable,
  onRetry,
}: {
  profile: GuestProfile
  membershipsUnavailable: boolean
  onRetry: () => void
}) {
  const t = useT()
  const memberships = profile.currentMemberships
  return (
    <Card>
      <CardHeader>
        <CardTitle level={2}>{t('guests.record.profile')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {membershipsUnavailable ? (
          <Notice tone="warning" title={t('guests.unavailable.membershipsTitle')}>
            {t('guests.unavailable.membershipsRecord')}{' '}
            <Button variant="link" size="sm" className="px-0 underline" onClick={onRetry}>
              {t('guests.unavailable.retry')}
            </Button>
          </Notice>
        ) : null}
        <dl className="flex flex-col gap-4">
          <ProfileRow label={t('guests.record.name')}>{guestName(t, profile.name)}</ProfileRow>
          {profile.customerNumber ? (
            <ProfileRow label={t('guests.record.customerNumber')}>
              <span className="tabular-nums">{profile.customerNumber}</span>
            </ProfileRow>
          ) : null}
          {profile.contact?.email ? <ProfileRow label={t('guests.record.email')}>{profile.contact.email}</ProfileRow> : null}
          {profile.contact?.mobile ? (
            <ProfileRow label={t('guests.record.phone')}>
              <span className="tabular-nums">{profile.contact.mobile}</span>
            </ProfileRow>
          ) : null}
          {membershipsUnavailable ? null : (
            <ProfileRow label={t('guests.record.membership', { count: Math.max(memberships.length, 1) })}>
              {memberships.length === 0 ? (
                t('guests.record.noMembership')
              ) : (
                <ul className="flex flex-col gap-2">
                  {memberships.map((membership, index) => (
                    <li key={`${membership.name}-${index}`} className="flex flex-col items-start gap-1">
                      <StatusPill tone="success">{membership.name}</StatusPill>
                      <span className="tabular-nums">
                        {t('guests.record.membershipDates', {
                          from: formatCalendarDate(membership.startDate),
                          until: formatCalendarDate(membership.endDate),
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </ProfileRow>
          )}
          {profile.sharedBy.length ? (
            <ProfileRow label={t('guests.record.sharedBy')}>
              <ul className="flex flex-col gap-1">
                {profile.sharedBy.map((sharer, index) => (
                  <li key={`${sharer.guestId ?? 'none'}-${index}`}>
                    <GuestLink guestId={sharer.guestId} name={guestName(t, sharer.name)} current={profile.guestId} />
                  </li>
                ))}
              </ul>
            </ProfileRow>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  )
}

function HistoryCard({
  guestId,
  timeZone,
  recordNotices,
  history,
  hasFeature,
}: {
  guestId: string
  timeZone: string
  recordNotices: GuestNotice[]
  history: ReturnType<typeof useGuestHistory>
  hasFeature: (featureId: string) => boolean
}) {
  const t = useT()
  const [filter, setFilter] = React.useState<HistoryFilter>('all')
  const historyFilters = React.useMemo(
    () =>
      HISTORY_FILTERS.filter((option) => {
        if (option === 'orders') return hasFeature(GUEST_RECORD_FEATURES.guestOrders)
        if (option === 'tables') return hasFeature(GUEST_RECORD_FEATURES.guestTables)
        return true
      }),
    [hasFeature],
  )
  React.useEffect(() => {
    if (!historyFilters.includes(filter)) setFilter('all')
  }, [filter, historyFilters])
  const visits = history.pages.flatMap((page) => page.data)
  const loaded = history.pages.length > 0

  const ctx: HistoryContext = {
    t,
    timeZone: history.pages[0]?.timeZone ?? timeZone,
    guestId,
    icons: ICONS,
    renderGuest: (member) => <GuestLink guestId={member.guestId} name={member.name} current={guestId} />,
  }
  const groups = historyGroups(ctx, visits, filter)
  const notShown = notShownLines(t, [
    ...recordNotices,
    ...history.pages.flatMap((page) => page.notices),
    ...visits.flatMap((visit) => visit.notices),
  ])
  const ordersUnavailable = visits.some((visit) => visit.unavailable.includes('orders'))

  let body: React.ReactNode
  if (!loaded && history.error) {
    body = (
      <OperationFeedback
        state="error"
        title={t('guests.history.loadFailedTitle')}
        detail={t('guests.history.loadFailedBody')}
        onRetry={() => void history.revalidate()}
      />
    )
  } else if (!loaded) {
    body = (
      <>
        <p className="sr-only" role="status" aria-live="polite">
          {t('guests.history.loading')}
        </p>
        <HistorySkeleton />
      </>
    )
  } else if (visits.length === 0) {
    body = <p className="text-sm text-fg-muted">{t('guests.history.noActivity')}</p>
  } else {
    body = (
      <div className="flex flex-col gap-4">
        {groups.length ? (
          <ActivityHistory
            groups={groups}
            labels={{
              showDetails: t('guests.history.showDetails'),
              hideDetails: t('guests.history.hideDetails'),
              items: t('guests.history.onThisVisit'),
            }}
          />
        ) : (
          <p className="text-sm text-fg-muted">{t('guests.history.nothingOfKind')}</p>
        )}
        {history.hasEarlier || (history.error && loaded) ? (
          <div className="flex flex-col items-start gap-2 border-t border-line pt-4">
            {history.error ? (
              <p role="alert" className="text-sm text-danger">
                {t('guests.history.earlierFailed')}
              </p>
            ) : null}
            <Button
              variant="secondary"
              disabled={history.loadingEarlier}
              onClick={() => (history.error ? void history.revalidate() : history.loadEarlier())}
            >
              {history.loadingEarlier ? t('guests.history.loadingEarlier') : t('guests.history.showEarlier')}
            </Button>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-end justify-between gap-3">
        <CardTitle level={2}>{t('guests.record.history')}</CardTitle>
        {loaded && visits.length > 0 ? (
          <Field id="guest-history-show" label={t('guests.history.show')} className="w-full sm:w-56">
            <NativeSelect value={filter} onChange={(event) => setFilter(event.target.value as HistoryFilter)}>
              {historyFilters.map((option) => (
                <option key={option} value={option}>
                  {t(`guests.history.filter.${option}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {notShown.length ? (
          <Notice tone="info" title={t('guests.history.notShownTitle')}>
            {notShown.join(' ')}
          </Notice>
        ) : null}
        {ordersUnavailable ? (
          <Notice tone="warning" title={t('guests.unavailable.ordersTitle')}>
            {t('guests.unavailable.ordersBody')}{' '}
            <Button variant="link" size="sm" className="px-0 underline" onClick={() => void history.revalidate()}>
              {t('guests.unavailable.retry')}
            </Button>
          </Notice>
        ) : null}
        {body}
      </CardContent>
    </Card>
  )
}
